// §V2 exact port — SUPPORT screen (docs/design-spikes/pomela-platform-v2/
// index.html lines 941-1000). Concierge two-column: hero + copy-email banner +
// response-time badge + "More from Pomela" links · right: REAL support form
// (same POST /v1/support flow as screens/Help.tsx — ticket + admin email).
// The statehost (loaded / success / error) is driven by the real request;
// v2.css's [data-statehost][data-state] rules show the matching panel.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useToast } from '@vibeshell/hooks';
import { Ic, tt } from './icons';
import { t } from '../lib/i18n';

const SUPPORT_EMAIL = 'support@pomela.app';

/** Spike topic rows mapped onto Help.tsx's exact /v1/support payload
 *  (topic key + email subject are the server contract — unchanged). */
const TOPICS = [
  { key: 'app_issue', subject: 'Help with an app I built', icon: 'wrench', tkey: 'v2.support.topicApp', en: "My app isn't working" },
  { key: 'billing', subject: 'Billing question', icon: 'card', tkey: 'v2.support.topicBilling', en: 'Billing question' },
  { key: 'idea', subject: 'An idea for Pomela', icon: 'bulb', tkey: 'v2.support.topicIdea', en: 'I have an idea' },
  { key: 'other', subject: 'Hello from Pomela', icon: 'msg', tkey: 'v2.support.topicOther', en: 'Something else' }
] as const;

/** The topic "Share an idea" leads to: the form below reaches the server, so an idea is heard, not kept on the device. */
const IDEA_TOPIC = TOPICS.find((tp) => tp.key === 'idea') ?? TOPICS[0];

type SupportState = 'loaded' | 'success' | 'error';

export function SupportV2(): JSX.Element {
  const { show } = useToast();
  const rootRef = useRef<HTMLElement>(null);
  const [topic, setTopic] = useState<(typeof TOPICS)[number]>(TOPICS[0]);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState<SupportState>('loaded');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  // scroll-reveal — same recipe as the spike's observeReveals(): the .sr
  // blocks rise in as they enter the #canvas viewport.
  useEffect(() => {
    const scope = rootRef.current;
    if (!scope) return;
    const els = Array.from(scope.querySelectorAll('.sr,.sr-stagger'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { root: document.getElementById('canvas'), threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    const raf = requestAnimationFrame(() => els.forEach((el) => io.observe(el)));
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  function copyEmail(): void {
    void navigator.clipboard
      ?.writeText(SUPPORT_EMAIL)
      .then(() => {
        navigator.vibrate?.(6);
        show(tt('v2.support.copied', 'Address copied ✓'), { variant: 'success' });
      })
      .catch(() => {
        /* clipboard blocked — the form is the primary path */
      });
  }

  // REAL submit — Help.tsx's exact flow: validate, POST /v1/support with
  // { email, subject, message, topic }, honest status handling (429 rate
  // message, server-provided message, network error).
  async function submit(): Promise<void> {
    if (sending) return;
    const em = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      show(t('help.errEmail'), { variant: 'error' });
      return;
    }
    if (message.trim().length < 5) {
      show(t('help.errMsg'), { variant: 'error' });
      return;
    }
    setSending(true);
    setErr('');
    try {
      const res = await fetch('/v1/support', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: em, subject: topic.subject, message: message.trim(), topic: topic.key })
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        setErr(j.message || (res.status === 429 ? t('help.errRate') : t('help.errGeneric')));
        setState('error');
        return;
      }
      navigator.vibrate?.(10);
      setState('success');
    } catch {
      setErr(t('help.errNetwork'));
      setState('error');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="screen on" data-screen="support" ref={rootRef}>
      <div className="wrap stack">
        <div className="grid-2 sr" style="align-items:start">
          <div className="stack" style="gap:20px">
            <div>
              <span className="sec-label" style="color:var(--brand)">{tt('v2.support.eyebrow', 'Concierge')}</span>
              <h2 className="title-hero" style="margin-top:10px">
                {tt('v2.support.hero1', "We're here.")}
                <br />
                {tt('v2.support.hero2', 'Always.')}
              </h2>
              <p className="sub-h" style="font-size:15px;max-width:44ch;margin-top:14px">
                {tt('v2.support.heroSub', 'Real humans behind every reply. The founder reads every message, and most get a thoughtful response within a day.')}
              </p>
            </div>
            <div className="banner info">
              <span className="b-ic"><Ic n="mail" /></span>
              <div>
                <b>{SUPPORT_EMAIL}</b>
                <p>{tt('v2.support.emailSub', 'Prefer email? Copy our address')}</p>
              </div>
              <button className="btn btn-tonal btn-sm mla" type="button" onClick={copyEmail}>
                <Ic n="copy" /> {tt('v2.support.copy', 'Copy')}
              </button>
            </div>
            {/* Help.tsx makes this exact promise today (help.responseTime) — badge kept. */}
            <span className="badge ok dot">{tt('v2.support.badge', 'Average response time: under 24 hours')}</span>
            <div>
              <span className="sec-label" style="display:block;margin-bottom:12px">{tt('v2.support.more', 'More from Pomela')}</span>
              <div className="panel list divide">
                <button
                  className="lrow clk"
                  type="button"
                  onClick={() => {
                    setTopic(IDEA_TOPIC);
                    setState('loaded');
                    requestAnimationFrame(() => document.getElementById('supportMsg')?.focus());
                  }}
                >
                  <span className="l-ico brand"><Ic n="msg" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.support.linkIdea', 'Share an idea')}</b>
                    <span>{tt('v2.support.linkIdeaSub', 'Tell us what Pomela should build next.')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" size={15} /></span>
                </button>
                <button className="lrow clk" type="button" onClick={() => { location.hash = 'guide'; }}>
                  <span className="l-ico brand"><Ic n="book" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.support.linkKb', 'Knowledge base')}</b>
                    <span>{tt('v2.support.linkKbSub', 'Walkthroughs, tips, and prompt patterns.')}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" size={15} /></span>
                </button>
                <button className="lrow clk" type="button" onClick={() => { location.href = `mailto:${SUPPORT_EMAIL}?subject=Press%20inquiry`; }}>
                  <span className="l-ico brand"><Ic n="ext" /></span>
                  <div className="l-txt">
                    <b>{tt('v2.support.linkPress', 'Press & partnerships')}</b>
                    <span>{tt('v2.support.linkPressSub', "We'd love to hear from journalists and creators.")}</span>
                  </div>
                  <span className="l-trail"><Ic n="chev" size={15} /></span>
                </button>
              </div>
            </div>
          </div>

          <div className="panel panel-p" data-statehost="support" data-state={state}>
            <div data-when="loaded" className="stack" style="gap:0">
              <span className="sec-label" style="display:block;margin-bottom:14px">{tt('v2.support.sendTitle', 'Send us a message')}</span>
              <div className="list" id="supportTopics" style="gap:8px;margin-bottom:20px" role="radiogroup" aria-label={tt('v2.support.sendTitle', 'Send us a message')}>
                {TOPICS.map((tp) => {
                  const sel = topic.key === tp.key;
                  return (
                    <button
                      key={tp.key}
                      className={`lrow clk${sel ? ' on' : ''}`}
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      data-topic={tp.en}
                      onClick={() => setTopic(tp)}
                    >
                      <span className={`l-ico${sel ? ' brand' : ''}`}><Ic n={tp.icon} /></span>
                      <div className="l-txt"><b>{tt(tp.tkey, tp.en)}</b></div>
                      <span className="l-trail"><Ic n={sel ? 'check' : 'chev'} size={15} /></span>
                    </button>
                  );
                })}
              </div>
              <div className="field">
                <label>{tt('v2.support.emailLabel', 'Your email')}</label>
                <input
                  className="input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                />
              </div>
              <div className="field">
                <label>{tt('v2.support.msgLabel', 'How can we help?')}</label>
                <textarea
                  className="textarea"
                  id="supportMsg"
                  placeholder={tt('v2.support.msgPh', "Tell us what's going on with your app…")}
                  value={message}
                  onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
                />
              </div>
              <button
                className="btn btn-solid btn-block btn-lg"
                id="supportSend"
                type="button"
                disabled={sending}
                aria-busy={sending}
                onClick={() => void submit()}
              >
                <Ic n="send" size={15} /> {sending ? tt('v2.support.sending', 'Sending…') : tt('v2.support.send', 'Send message')}
              </button>
            </div>
            <div data-when="success">
              <div className="empty" style="padding:40px 16px">
                <span className="e-ic e-ic-ok"><Ic n="check" size={22} /></span>
                <h4>{tt('v2.support.successTitle', 'Message sent')}</h4>
                <p>{tt('v2.support.successBody', "We'll reply within a day. Keep an eye on the inbox at the email you gave us.")}</p>
                <button
                  className="btn btn-tonal"
                  type="button"
                  onClick={() => {
                    setMessage('');
                    setState('loaded');
                  }}
                >
                  <Ic n="arrowl" size={15} /> {tt('v2.support.sendAnother', 'Send another')}
                </button>
              </div>
            </div>
            <div data-when="error">
              <div className="empty" style="padding:40px 16px">
                <span className="e-ic e-ic-danger"><Ic n="x" size={22} /></span>
                <h4>{tt('v2.support.errTitle', "That didn't send")}</h4>
                <p>{err || tt('v2.support.errBody', 'Something went wrong on our side. Please check your connection and try again.')}</p>
                <button className="btn btn-tonal" type="button" onClick={() => setState('loaded')}>
                  <Ic n="history" size={15} /> {tt('v2.support.tryAgain', 'Try again')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <p style="text-align:center;color:var(--faint);font-size:12.5px">{tt('v2.support.foot', 'Built with care in 2026')} · {SUPPORT_EMAIL}</p>
      </div>
    </section>
  );
}
